/****************************************************
 * Module Name:  MarketSellWolzaModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useMemo, useState } from "react";
import { Box, styled, Tooltip, } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import renderCellExpand from "./dataGridCellExpand";
import { DataGridPro, GridToolbar, gridClasses } from "@mui/x-data-grid-pro";
import WolzaIndiaExportProxy from "../../utility/proxy/nivodautility/wolzaIndiaExportProxy";
import dayjs from "dayjs";

const StyledDataGridPro = styled(DataGridPro)({
    '.MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
        display: 'none',
    },
    '.MuiDataGrid-menuIcon': {
        width: '1px',
        fontSize: '1px'
    },
    [`& .${gridClasses.menuIconButton}`]: {
        marginRight: '-2px',
        fontSize: '1px',
        width: '1px',
        height: '1px',
    }
})

const MarketSellWolzaModal = ({
    stone,
    loadedCustomers,
    fromDate,
    toDate,
    mappingkey,
    stoneType
}) => {
    const [listData, setListData] = useState([]);
    const [listLoader, setListLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});

    // eslint-disable-next-line
    const [local_Permission, setLocal_Permission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MASTER_POTENTIALSELL
        )
    );

    useEffect(() => {
        handleSearch();
    }, []);


    const handleSearch = () => {
        const data = formatDateRange(fromDate, toDate);
        let customerid = null;
        setListLoader(true);

        if (mappingkey === "hasMapping") {
            customerid = loadedCustomers.customer[0].value;
            WolzaIndiaExportProxy.GetCustomersWolzaDetails(customerid, data, stoneType, (responseData) => {
                processResponse(responseData);
            });
        } else if (mappingkey === "withoutMapping") {
            customerid = loadedCustomers.customer[0].value;
            WolzaIndiaExportProxy.GetConsigneeWolzaDetails(customerid, data, stoneType, (responseData) => {
                processResponse(responseData);
            });
        } else {
            let stoneData = {
                stoneid: stone.stoneid,
                color: stone.color,
                clarity: stone.clarity,
                shape: stone.shape,
                cut: stone.cut,
                sym: stone.sym,
                flo: stone.flo,
                pol: stone.pol,
                sizemasterid: stone.size,
                from: data.from,
                to: data.to,
                lab: stone.lab,
                stonetype: stone.stonetype,
            }
            WolzaIndiaExportProxy.GetWolzaDetailsByCriteria(stoneData, (responseData) => {
                processResponse(responseData);
            });
        }
    };

    const formatDateRange = (fromDate, toDate) => {
        return {
            from: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
            to: toDate ? dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
        };
    };

    const processResponse = (responseData) => {
        if (responseData) {
            const uniqueFilterValues = getUniqueFilterValues(responseData);
            setFilterValues(uniqueFilterValues);
            setListData(responseData);
        }
        setListLoader(false);
    };

    const getUniqueFilterValues = (data) => {
        return {
            companyname: Utility.getDistinctOptions(data, "companyname"),
            location: Utility.getDistinctOptions(data, "location"),
            countryofdestination: Utility.getDistinctOptions(data, "countryofdestination"),
            shippercity: Utility.getDistinctOptions(data, "shippercity"),
            shippername: Utility.getDistinctOptions(data, "shippername"),
            shape: Utility.getDistinctOptions(data, "shape"),
            cut: Utility.getDistinctOptions(data, "cut"),
            clarity: Utility.getDistinctOptions(data, "clarity"),
            pol: Utility.getDistinctOptions(data, "pol"),
            sym: Utility.getDistinctOptions(data, "sym"),
            flo: Utility.getDistinctOptions(data, "flo"),
            color: Utility.getDistinctOptions(data, "color"),
            lab: Utility.getDistinctOptions(data, "lab"),
        };
    };

    const columns = useMemo(() =>
        [
            {
                field: "id",
                headerName: "ID",
                headerAlign: "center",
                align: "center",
                type: "number",
                valueFormatter: (params) => params.value,
                minWidth: 50,
            },
            {
                field: "companyname",
                headerName: "CUSTOMER NAME",
                headerAlign: "center",
                align: "center",
                minWidth: 150,
                flex: 1,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.companyname : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "date",
                headerAlign: "center",
                align: "center",
                headerName: "DATE",
                width: 100,
                renderCell: renderCellExpand,
            },
            {
                field: "estimatedfob",
                headerName: "ESTIMATED FOB",
                headerAlign: "center",
                align: "center",
                minWidth: 60,
                flex: 1,
                type: "number",
                valueFormatter: (params) => params.value,
                renderCell: renderCellExpand,
            },
            {
                field: "shippercity",
                headerName: "SHIPPER CITY",
                headerAlign: "center",
                align: "center",
                minWidth: 60,
                flex: 1,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.shippercity : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "shippername",
                headerName: "SHIPPER NAME",
                align: "center",
                headerAlign: "center",
                minWidth: 60,
                flex: 1,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.shippername : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
                filterable: true,
            },
            {
                field: "certificateid",
                headerName: "CERT",
                headerAlign: "center",
                align: "center",
                minWidth: 60,
                flex: 1,
                renderCell: (params) => (params.value && params.value.trim() !== "" && params.value !== "null") ? params.value : "",
            },
            {
                field: "countryofdestination",
                headerName: "COUNTRY OF DESTINATION",
                headerAlign: "center",
                align: "center",
                minWidth: 60,
                flex: 1,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.countryofdestination : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "qty",
                headerName: "QTY",
                headerAlign: "center",
                align: "center",
                minWidth: 40,
                type: "number",
                valueFormatter: (params) => params.value,
                flex: 1,
                renderCell: renderCellExpand,
            },
            {
                field: "shape",
                headerName: "SHAPE",
                headerAlign: "center",
                align: "center",
                width: 65,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.shape : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "color",
                headerName: "COL",
                headerAlign: "center",
                align: "center",
                width: 10,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.color : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "clarity",
                headerName: "CLR",
                headerAlign: "center",
                align: "center",
                width: 55,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.clarity : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "cut",
                headerName: "CUT",
                headerAlign: "center",
                align: "center",
                width: 30,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.cut : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "pol",
                headerName: "POL",
                headerAlign: "center",
                align: "center",
                width: 30,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.pol : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "sym",
                headerName: "SYM",
                headerAlign: "center",
                align: "center",
                width: 30,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.sym : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "flo",
                headerName: "FLO",
                headerAlign: "center",
                align: "center",
                width: 60,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.flo : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
            {
                field: "lab",
                headerName: "LAB",
                headerAlign: "center",
                align: "center",
                width: 60,
                type: "singleSelect",
                valueOptions: filterValues ? filterValues.lab : [],
                filterOperators: Utility.combinedFilters(),
                renderCell: renderCellExpand,
            },
        ], [filterValues]);

    const rows = listData;

    return (

        <div>
            <div>
                <Box
                    sx={{
                        px: 1,
                        width: "100%",
                        position: "relative",
                        backgroundColor: '#fff',
                        color: '#000',
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "5px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: "75vh",
                    }}
                >
                    <StyledDataGridPro
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "6px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },
                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        density="compact"
                        disableRowSelectionOnClick={true}
                        slots={{ toolbar: GridToolbar }}
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={listLoader}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    locationid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500,
                                },
                            },
                        }}
                    />
                    <Box
                        style={{
                            position: "absolute",
                            top: 5,
                            right: "270px",
                        }}
                    >
                        {listLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => handleSearch()}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            </Tooltip>
                        )}

                    </Box>
                </Box>
            </div>
        </div>
    );
};
export default MarketSellWolzaModal;
